// app/(protected)/(settings)/settings/custom-fields/types.ts

export type DbContactCustomFieldType =
  | "text"
  | "number"
  | "date"
  | "boolean"
  | "single_select"
  | "multi_select"
  | "radio";

export type UiCustomFieldKind =
  | "single_line"
  | "multi_line"
  | "number"
  | "currency"
  | "phone"
  | "select_single"
  | "select_multiple"
  | "radio"
  | "checkbox"
  | "datetime"
  | "document"
  | "signature";

export const KEY_MAX_LEN = 80;

export type KindExample = {
  label: string;
  defaultValue?: string;
  options?: string[];
};

export const UI_FIELD_KIND_LABELS: Record<UiCustomFieldKind, string> = {
  single_line: "Ligne unique",
  multi_line: "Lignes multiples",
  number: "Nombre",
  currency: "Montant",
  phone: "Numéro",
  select_single: "Liste déroulante (simple)",
  select_multiple: "Liste déroulante (multiple)",
  radio: "Boutons radio",
  checkbox: "Cases à cocher",
  datetime: "Date & heure",
  document: "Fichier à joindre",
  signature: "Signature",
};

/**
 * One choice in a select / radio / multi-select option list.
 * `id` is opaque (uuid) and stable: renaming the label keeps every contact's
 * stored reference valid. Deletion is the only destructive op.
 */
export type CustomFieldOption = {
  id: string;
  label: string;
};

export type ContactCustomFieldOptions = {
  // historique: certains anciens formats peuvent être { ui: { kind: "..." } }
  ui: UiCustomFieldKind | { kind: UiCustomFieldKind };
  /** Each entry is `{ id, label }` — canonical shape. The DB has been
   *  fully migrated; no more legacy `string[]` form circulates. */
  values?: CustomFieldOption[];
  /** Default option(s): id (single) or id[] (multi). */
  default?: unknown;
  currency?: string;
  bucket?: string;
};

/** Validates an `options.values` array, dropping any entries that don't
 *  match the canonical `{ id, label }` shape. */
export function normalizeOptionsValues(
  raw: unknown,
): CustomFieldOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (
        entry &&
        typeof entry === "object" &&
        "id" in entry &&
        "label" in entry &&
        typeof (entry as { id?: unknown }).id === "string" &&
        typeof (entry as { label?: unknown }).label === "string"
      ) {
        return {
          id: (entry as { id: string }).id,
          label: (entry as { label: string }).label,
        };
      }
      return null;
    })
    .filter((x): x is CustomFieldOption => x !== null);
}

export type ContactCustomFieldRow = {
  id: string;
  org_id: string;
  key: string;
  label: string;
  type: DbContactCustomFieldType;
  options: ContactCustomFieldOptions;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type CreateCustomFieldInput = {
  label: string;
  kind: UiCustomFieldKind;
  optionValues?: string[];
  defaultValue?: unknown;
};

export type UpdateCustomFieldInput = {
  id: string;
  label?: string;
  options?: ContactCustomFieldOptions;
};

export const UI_KIND_TO_DB: Record<
  UiCustomFieldKind,
  { type: DbContactCustomFieldType; config?: Record<string, unknown> }
> = {
  single_line: { type: "text", config: { ui: "single_line" } },
  multi_line: { type: "text", config: { ui: "multi_line" } },
  number: { type: "number", config: { ui: "number" } },
  currency: { type: "number", config: { ui: "currency", currency: "EUR" } },
  phone: { type: "text", config: { ui: "phone" } },

  select_single: { type: "single_select", config: { ui: "select_single" } },
  select_multiple: { type: "multi_select", config: { ui: "select_multiple" } },
  radio: { type: "radio", config: { ui: "radio" } },

  checkbox: { type: "boolean", config: { ui: "checkbox" } },
  datetime: { type: "date", config: { ui: "datetime" } },

  document: {
    type: "text",
    config: { ui: "document", bucket: "contacts-documents" },
  },
  signature: {
    type: "text",
    config: { ui: "signature", bucket: "contacts-signatures" },
  },
};

export function displayKindFromRow(row: ContactCustomFieldRow): UiCustomFieldKind {
  const opts =
    row?.options && typeof row.options === "object" && !Array.isArray(row.options)
      ? row.options
      : null;

  const ui = opts?.ui;

  // nouveau format: ui: "single_line"
  if (typeof ui === "string") {
    const maybe = ui as UiCustomFieldKind;
    if (maybe in UI_FIELD_KIND_LABELS) return maybe;
  }

  // format legacy: ui: { kind: "single_line" }
  if (ui && typeof ui === "object" && typeof ui.kind === "string") {
    const maybe = ui.kind as UiCustomFieldKind;
    if (maybe in UI_FIELD_KIND_LABELS) return maybe;
  }

  switch (row.type) {
    case "single_select":
      return "select_single";
    case "multi_select":
      return "select_multiple";
    case "radio":
      return "radio";
    case "boolean":
      return "checkbox";
    case "date":
      return "datetime";
    case "number":
      return "number";
    case "text":
    default:
      return "single_line";
  }
}

/**
 * Canonical slug used for Contact Custom Field keys.
 * Must be the single source of truth for key generation.
 */
export function slugifyKey(input: string): string {
  const s = (input ?? "").trim();
  if (!s) return "";
  return s
    .replace(/œ/gi, "oe")
    .replace(/æ/gi, "ae")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_");
}

function parseBoolishString(s: string): boolean | undefined {
  const v = s.trim().toLowerCase();
  if (!v) return undefined;
  if (["1", "true", "oui", "yes", "on"].includes(v)) return true;
  if (["0", "false", "non", "no", "off"].includes(v)) return false;
  return undefined;
}

function normalizeDefaultValue(kind: UiCustomFieldKind, raw: unknown): unknown | undefined {
  if (raw === null || raw === undefined) return undefined;

  if (kind === "checkbox") {
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "string") return parseBoolishString(raw);
    return undefined;
  }

  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return undefined;

    if (kind === "number" || kind === "currency") {
      const n = Number(s.replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    }

    if (kind === "datetime") {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }

    if (kind === "select_multiple") {
      const arr = s
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      return arr.length ? arr : undefined;
    }

    return s;
  }

  if (kind === "select_multiple" && Array.isArray(raw)) {
    const arr = raw.map((x) => String(x).trim()).filter(Boolean);
    return arr.length ? arr : undefined;
  }

  if ((kind === "select_single" || kind === "radio") && typeof raw !== "string") {
    return String(raw).trim() || undefined;
  }

  return raw;
}

/**
 * Build payload for DB insert.
 * Key is deterministic and derived from the label (no user override).
 */
function newOptionId(): string {
  // crypto.randomUUID is available in Node and modern browsers.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for very old environments.
  return `opt-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function buildDbPayload(input: CreateCustomFieldInput): {
  key: string;
  label: string;
  type: DbContactCustomFieldType;
  options: ContactCustomFieldOptions;
} {
  const spec = UI_KIND_TO_DB[input.kind];

  const needsListOptions =
    input.kind === "select_single" ||
    input.kind === "select_multiple" ||
    input.kind === "radio";

  const label = input.label.trim();
  if (!label) throw new Error("Le nom de l’information est requis.");

  const key = slugifyKey(label).slice(0, KEY_MAX_LEN).replace(/_+$/g, "");
  if (!key) throw new Error("Ce nom ne peut pas être utilisé. Choisissez-en un autre.");

  const options: ContactCustomFieldOptions = {
    ...(spec.config ?? { ui: input.kind }),
  } as ContactCustomFieldOptions;

  let values: CustomFieldOption[] = [];

  if (needsListOptions) {
    const labels = (input.optionValues ?? [])
      .map((v) => v.trim())
      .filter(Boolean);

    if (labels.length < 1) {
      throw new Error("Veuillez ajouter au moins une option.");
    }

    // Reject exact-duplicate labels at creation — admins can rename later if
    // they want two options to differ only by case/punctuation.
    const seen = new Set<string>();
    for (const l of labels) {
      const k = l.toLowerCase();
      if (seen.has(k)) {
        throw new Error(`Option en double : "${l}". Chaque option doit être unique.`);
      }
      seen.add(k);
    }

    values = labels.map((label) => ({ id: newOptionId(), label }));
    options.values = values;
  }

  const dv = normalizeDefaultValue(input.kind, input.defaultValue);

  if (dv !== undefined && needsListOptions) {
    if (input.kind === "select_single" || input.kind === "radio") {
      const s = String(dv).trim();
      const match = values.find((v) => v.label === s);
      if (!match) {
        throw new Error("La valeur par défaut doit être une des options.");
      }
      // Default is stored as the option's id (stable across renames).
      options.default = match.id;
    }

    if (input.kind === "select_multiple") {
      const arr = Array.isArray(dv)
        ? dv.map((x) => String(x).trim()).filter(Boolean)
        : [];
      const ids: string[] = [];
      for (const lbl of arr) {
        const m = values.find((v) => v.label === lbl);
        if (!m) {
          throw new Error(`Valeur par défaut invalide : ${lbl}`);
        }
        ids.push(m.id);
      }
      if (ids.length) options.default = ids;
    }
  } else if (dv !== undefined) {
    options.default = dv;
  }

  return {
    key,
    label,
    type: spec.type,
    options,
  };
}